import { Accordion, AccordionDetails, AccordionSummary, Alert, Stack } from "@mui/material";
import { Empty, Flex } from "@zfp-akademie-a-s/zfp-design";
import { ChevronDownIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { ListRemoveIcon } from "Components/_Shared/Icons/Icons";
import type {
  ContractProposalBaseSolutionHorizonType,
  ContractProposalListItem,
  CurrentContractListItem,
  GoalOverview,
  NsurePlanOverview,
} from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrencyShortText } from "Infrastructure/Utils/CurrencyUtils";
import type { PropsWithChildren, ReactElement } from "react";
import { GoalHorizonHeader } from "../_Shared/Goals/GoalHorizonHeader";
import { goalHorizonHeaderStyles } from "../_Shared/Goals/constants";
import type { GoalHorizonHeaderData } from "../_Shared/Goals/types";
import { getGoalTargetAmount } from "../_Shared/Goals/utils";
import { GoalFulfillmentItem } from "./GoalFulfillmentItem";
import { GoalFulfillmentSolutions } from "./GoalFulfillmentSolutions";
import { GoalFulfillmentFilterType, useGoalsFulfillmentContext } from "./GoalsFulfillmentContext";
import { SolutionType } from "./types";

const GoalsAccordionSummary = ({ children }: PropsWithChildren) => {
  return (
    <AccordionSummary
      expandIcon={<ChevronDownIcon />}
      sx={{
        ...goalHorizonHeaderStyles,
        flexDirection: "row-reverse",
        "& .MuiAccordionSummary-content": {
          margin: 0,
        },
        "& .MuiAccordionSummary-expandIconWrapper": {
          color: "primary.main",
          marginRight: 2,
          marginLeft: 1,
        },
      }}
    >
      {children}
    </AccordionSummary>
  );
};

type GoalsAccordionProps = {
  children: ReactElement | ReactElement[];
  defaultExpanded: boolean;
};

const GoalsAccordion = ({ children, defaultExpanded }: GoalsAccordionProps) => {
  return (
    <Accordion
      disableGutters
      defaultExpanded={defaultExpanded}
      square
      sx={{
        boxShadow: "none",
        "&::before": {
          display: "none",
        },

        "& + &": {
          marginTop: "5px",
        },
      }}
    >
      {children}
    </Accordion>
  );
};

type Props = {
  goals: GoalOverview[];
  horizonType: ContractProposalBaseSolutionHorizonType;
  contractProposals: ContractProposalListItem[];
  currentContracts: CurrentContractListItem[];
  futureAmount?: number;
  nsurePlan?: NsurePlanOverview;
  handleCurrentSolutionDrawerOpen: (type: ContractProposalBaseSolutionHorizonType) => void;
  handleProposedSolutionDrawerOpen: (type: ContractProposalBaseSolutionHorizonType) => void;
};

export const GoalsFulfillmentTerm = ({
  goals,
  horizonType,
  contractProposals,
  currentContracts,
  futureAmount,
  nsurePlan,
  handleCurrentSolutionDrawerOpen,
  handleProposedSolutionDrawerOpen,
}: Props) => {
  const { t } = useResource();
  const { isFilterSelected } = useGoalsFulfillmentContext();

  const hasGoals = goals.length > 0;
  const goalsTotalAmount = goals?.reduce((acc, item) => acc + (getGoalTargetAmount(item) ?? 0), 0);

  const hasSolutions = !!(currentContracts && currentContracts.length > 0) || !!(contractProposals && contractProposals.length > 0);

  const shouldShowFutureAmount = isFilterSelected(GoalFulfillmentFilterType.FutureAmount);

  const getHeaderData = (): GoalHorizonHeaderData[] => [
    ...(shouldShowFutureAmount
      ? [
          {
            title: t(Resources.AnalysisDetail.GoalsFulfillment.FutureAmount),
            value: futureAmount != null ? formatCurrencyShortText(futureAmount) : "-",
          },
        ]
      : []),
    {
      title: t(Resources.AnalysisDetail.GoalsFulfillment.TargetAmount),
      value: goalsTotalAmount ? formatCurrencyShortText(goalsTotalAmount) : "-",
    },
  ];

  return (
    <GoalsAccordion defaultExpanded={hasGoals || hasSolutions}>
      <GoalsAccordionSummary>
        <GoalHorizonHeader title={t(Resources.AnalysisDetail.GoalsFulfillment[horizonType])} data={getHeaderData()} />
      </GoalsAccordionSummary>

      <AccordionDetails sx={{ px: 0 }}>
        {hasGoals || hasSolutions ? (
          <Stack spacing={Spacing.XXL} pl={Spacing.XL} pb={Spacing.L}>
            {hasGoals ? (
              <Stack spacing={Spacing.M}>
                {goals?.map((goal) => (
                  <GoalFulfillmentItem key={`${goal.guid}_${goal.groupGuid ?? ""}`} goal={goal} />
                ))}
              </Stack>
            ) : (
              <Flex pt={Spacing.XL}>
                <Alert severity="warning">{t(Resources.AnalysisDetail.GoalsFulfillment.NoGoalsInHorizonYet)}</Alert>
              </Flex>
            )}

            <GoalFulfillmentSolutions
              horizonType={horizonType}
              solutionType={SolutionType.Current}
              currentContracts={currentContracts}
              onClickAdd={() => handleCurrentSolutionDrawerOpen(horizonType)}
              isAddDisabled={!hasGoals}
            />

            <GoalFulfillmentSolutions
              horizonType={horizonType}
              solutionType={SolutionType.Proposed}
              contractProposals={contractProposals}
              onClickAdd={() => handleProposedSolutionDrawerOpen(horizonType)}
              nsurePlan={nsurePlan}
              isAddDisabled={!hasGoals}
            />
          </Stack>
        ) : (
          <Empty
            text={t(Resources.AnalysisDetail.GoalsFulfillment.NoGoalsInHorizon)}
            iconSize={40}
            icon={<ListRemoveIcon sx={{ color: "grey.400" }} />}
          />
        )}
      </AccordionDetails>
    </GoalsAccordion>
  );
};
